import React from "react";
import type { Localizer } from "@/main/react/Types";
import CreateSeriesForm from "@/main/react/series/CreateSeriesFormLazy";
import SimpleDialog, { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import { emitSeriesSettingsUpdated } from "@/main/common/EventRegistry";
import { GuideLink } from "@/main/react/guides/GuideLink";

type ShowUpdateSeriesSettingsDialogProps = {
    seriesPublicId?: string;
    title?: string;
    description?: string;
    thumbnailUrl?: string;
    mainLocale?: string;
    privateAccess?: boolean;
    bookmarksCollection?: boolean;
    sensitive?: boolean;
    localizer: Localizer;
};

export function showUpdateSeriesSettingsDialog({
    seriesPublicId,
    title = "",
    description = "",
    thumbnailUrl = "",
    mainLocale,
    privateAccess = false,
    bookmarksCollection = false,
    sensitive = false,
    localizer,
}: ShowUpdateSeriesSettingsDialogProps) {
    const isEditing = Boolean(seriesPublicId);

    return openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={
                    <div id="update-series-dialog-title">
                    {/* `SERIES-45` (r41 C16): the sheet names its object ("Edit series"); the header
                        button that opens it keeps `series.edit`. */}
                    {isEditing
                        ? (localizer("series.settings.edit_title"))
                        : (localizer("create.create-series.page-title"))}
                </div>
            }
            width="6xl"
        >
            {/* Creating only — see the sibling group dialog for why. A series is the least
                self-evident object in the product, so this is the pointer with the most to earn. */}
            {!isEditing && (
                <div className="mb-3">
                    <GuideLink
                        localizer={localizer}
                        guide="series"
                        textKey="guides.link.series"
                        surface="create-series"
                        // `DASHBOARD-116` (r41 V6 a): the guide opens beside the dialog, so reading
                        // it never discards a half-filled form.
                        openInNewTab
                    />
                </div>
            )}
            <CreateSeriesForm
                seriesPublicId={seriesPublicId}
                title={title}
                description={description}
                thumbnailUrl={thumbnailUrl}
                mainLocale={mainLocale}
                privateAccess={privateAccess}
                bookmarksCollection={bookmarksCollection}
                sensitive={sensitive}
                localizer={localizer}
                onCancel={() => onOpenChange(false)}
                onSaved={(result) => {
                    onOpenChange(false);
                    if (isEditing && result?.series?.seriesPublicId) {
                        emitSeriesSettingsUpdated({
                            seriesPublicId: result.series.seriesPublicId,
                            title: result.series.title,
                            description: result.series.description ?? null,
                            thumbnailUrl: result.series.thumbnailUrl ?? null,
                            mainLocale: result.series.mainLocale ?? null,
                            privateAccess: result.series.privateAccess,
                            bookmarksCollection: result.series.bookmarksCollection,
                            sensitive: result.series.sensitive,
                        });
                    }
                }}
            />
        </SimpleDialog>
    ));
}

export function showCreateSeriesDialog(localizer: Localizer) {
    return showUpdateSeriesSettingsDialog({ localizer });
}
